import { Box, Chip, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MessageGroup from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup';
import SendButton from 'components/@home/@messages/ChatPanel/SendForm/buttons/SendButton';
import ChatInput from 'components/@home/@messages/ChatPanel/SendForm/controls/ChatField/ChatInput';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import ArrowBackIcon from 'mdi-react/ArrowBackIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import ReloadIcon from 'mdi-react/ReloadIcon';
import { object } from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { compileChatFlowStoredMessage } from 'utils/chatflowTemplateCompile';
import { I18n } from 'utils/i18n';
import connector from './connector';
import omit from 'lodash/omit';

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
    borderLeft: `solid 1px ${theme.palette.secondary.extraLight}`,
  },
  title: {
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    color: theme.palette.primary.ultraDark,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  close: {
    color: theme.palette.primary.ultraDark,
  },
  bodyWrapper: {
    flexGrow: 1,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    '&:before': {
      content: `''`,
      position: 'absolute',
      boxShadow: 'inset -2px 0 6px 1px rgba(0,0,0,0.2)',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 14,
      pointerEvents: 'none',
    },
  },
  body: {
    flexGrow: 1,
    overflow: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    backgroundColor: theme.palette.secondary.ultraLight,
  },
  context: {
    padding: theme.spacing(2),
    fontSize: '0.7em',
    color: theme.palette.text.primary,
  },
  chatInput: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  send: {
    flexGrow: 1,
  },
  suggestions: {
    padding: theme.spacing(2),
    lineHeight: 2.5,
  },
  suggestion: {
    marginRight: theme.spacing(1),
  },
});

const ChatFlowTest = ({ flow, actions, authEmployee }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const initialValue = { text: '' };
  const bodyRef = useRef();
  const [page, setPage] = useState(null);
  const [inputValue, setInputValue] = useState(initialValue);
  const [messages, setMessages] = useState([]);
  const [conversationContext, setConversationContext] = useState({});
  const suggestionsPortal = useRef();
  const { context } = useContext(ChatFlowContext);
  const pushMessage = (message, isMine = false) =>
    setMessages(m => [
      ...m,
      { _id: `${Math.random()}`, employeeName: isMine ? undefined : flow.name, ...message, isMine },
    ]);
  const templateCtx = base => (authEmployee ? { ...base, user: authEmployee } : { ...base });

  useEffect(() => {
    const start = flow.pages?.find(p => p._id === flow.startPage);
    if (start) {
      setPage(start);
      start.entryMessages?.forEach(m => {
        pushMessage(compileChatFlowStoredMessage(m, templateCtx({})));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const element = bodyRef.current;
    element.scrollTop = element.scrollHeight;
  }, [messages]);

  const handleSubmit = async (e, textIn) => {
    e.preventDefault();
    const text = textIn || inputValue.text;
    setInputValue(initialValue);
    pushMessage({ text }, true);
    const { value } = await actions.chatFlows.message(
      flow._id,
      text,
      page._id,
      conversationContext,
    );
    let nextCtx = { ...conversationContext, ...value.context };
    if (value.entities) {
      nextCtx = { ...nextCtx, entities: value.entities };
    }
    if (value.slotFill) {
      nextCtx = { ...nextCtx, slotFill: value.slotFill };
    } else {
      nextCtx = omit(nextCtx, 'slotFill');
    }
    const route = value.matchedRoute;
    if (route) {
      route.replies.forEach(r => {
        pushMessage(r);
      });
      if (route.transition === flow.startPage) {
        setConversationContext({});
        const start = flow.pages?.find(p => p._id === flow.startPage);
        setPage(start);
        start?.entryMessages?.forEach(m => {
          pushMessage(compileChatFlowStoredMessage(m, templateCtx({})));
        });
      } else if (route.transition) {
        setConversationContext(nextCtx);
        const newPage = flow.pages?.find(p => p._id === route.transition);
        setPage(newPage);
        newPage?.entryMessages?.forEach(m => {
          pushMessage(compileChatFlowStoredMessage(m, templateCtx(nextCtx)));
        });
      } else {
        setConversationContext(nextCtx);
      }
    } else {
      setConversationContext(nextCtx);
      pushMessage({ text: 'Lo siento no te he entendido :(' });
    }
  };
  const listenForEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  const lastMessage = messages[messages.length - 1];
  return (
    <Box component="form" onSubmit={handleSubmit} sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Box sx={styles.headerLeft}>
          {context.length > 1 && (
            <IconButton
              sx={styles.close}
              edge="start"
              aria-label="close"
              onClick={context.pop}
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography size="small" sx={styles.title}>
            {I18n.t('ChatFlowSettings.Test flow')}
          </Typography>
          <IconButton
            sx={styles.close}
            edge="end"
            aria-label="close"
            onClick={() => {
              setPage(null);
              setConversationContext({});
              setMessages([]);
              setTimeout(() => {
                const start = flow.pages?.find(p => p._id === flow.startPage);
                setPage(start);
                start?.entryMessages?.forEach(m => {
                  pushMessage(compileChatFlowStoredMessage(m, templateCtx({})));
                });
              }, 10);
            }}
            size="large"
          >
            <ReloadIcon />
          </IconButton>
        </Box>
        <div>
          <IconButton
            sx={styles.close}
            edge="end"
            aria-label="close"
            onClick={context.clear}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </Box>
      <Box sx={styles.context}>
        Context:
        <pre>
          {(() => {
            const { ...rest } = conversationContext;
            return JSON.stringify(rest, undefined, 2);
          })()}
        </pre>
        {page && <div>Page: {page.name}</div>}
      </Box>
      <Box component="section" sx={styles.bodyWrapper}>
        <Box sx={styles.body} ref={bodyRef}>
          <br />
          {messages.map(message => (
            <MessageGroup key={message._id} message={message} />
          ))}
        </Box>
      </Box>
      <Box component="footer">
        <div ref={suggestionsPortal} />
        {!!lastMessage?.suggestions?.length && (
          <Box sx={styles.suggestions}>
            {lastMessage.suggestions.map(s => (
              <Chip
                sx={styles.suggestion}
                key={s}
                label={s}
                clickable
                onClick={async e => {
                  await handleSubmit(e, s);
                }}
              />
            ))}
          </Box>
        )}
        <Box sx={styles.chatInput}>
          <Box sx={styles.send}>
            <ChatInput
              autoFocus
              onKeyPress={listenForEnter}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              name="text"
              placeholder={I18n.t('ChatFlowSettings.Write message')}
              suggestionsPortal={suggestionsPortal}
            />
          </Box>
          <SendButton />
        </Box>
      </Box>
    </Box>
  );
};

ChatFlowTest.propTypes = {
  actions: object.isRequired,
  authEmployee: object,
  flow: object.isRequired,
};
export default connector(ChatFlowTest);
